import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { apiClient } from '../utils/apiUtils';

const ForgotPasswordModal = ({ show, onHide }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleClose = () => {
    // Reset all states
    setStep(1);
    setEmail('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    onHide();
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/forgot-password', {
        email: email.toLowerCase().trim()
      }, {
        timeout: 120000 // 120 seconds
      });

      setSuccess(response.data.message);
      setStep(2);
    } catch (error) {
      console.error('❌ Forgot password request failed:', error);

      if (error.code === 'ECONNABORTED') {
        setError('Request timed out. The server may be starting up. Please wait and try again.');
      } else if (error.response) {
        setError(error.response.data?.error || 'Failed to send verification code');
      } else if (error.request) {
        setError('Cannot reach the server. Please check your connection and try again.');
      } else {
        setError('Failed to send verification code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/auth/reset-password-with-code', {
        email: email.toLowerCase().trim(),
        code: verificationCode.trim(),
        newPassword
      });

      setSuccess(response.data.message);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('❌ Password reset failed:', error);

      if (error.response) {
        setError(error.response.data?.error || 'Failed to reset password');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setError('');
    setSuccess('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      style={{ zIndex: 10100 }}
      backdrop="static"
      keyboard={false}
      backdropClassName="modal-backdrop"
    >
      <Modal.Header 
        closeButton 
        style={{ 
          position: 'relative', 
          zIndex: 10120, 
          backgroundColor: '#fff' 
        }}
      >
        <Modal.Title>
          <i className="bi bi-key me-2"></i>
          Forgot Password
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ position: 'relative', zIndex: 10120, backgroundColor: '#fff' }}>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        {step === 1 && (
          <Form onSubmit={handleSendCode}>
            <div className="text-center mb-4">
              <div className="mb-3" style={{ fontSize: '48px', color: '#dc3545' }}>
                <i className="bi bi-envelope-exclamation"></i>
              </div>
              <h5>Reset Your Password</h5>
              <p className="text-muted">
                Enter your email address and we'll send you a verification code to reset your password.
              </p>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading || !email}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending Code...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send me-2"></i>
                    Send Verification Code
                  </>
                )}
              </Button>
              <Button variant="secondary" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
            </div>
          </Form>
        )}

        {step === 2 && (
          <Form onSubmit={handleVerifyAndReset}>
            <div className="text-center mb-4">
              <div className="mb-3" style={{ fontSize: '48px', color: '#28a745' }}>
                <i className="bi bi-shield-lock"></i>
              </div>
              <h5>Create New Password</h5>
              <p className="text-muted">
                Enter the verification code sent to <strong>{email}</strong> and your new password.
              </p>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Verification Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength="6"
                required
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Check your email inbox (and spam folder) for the code.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter new password (min. 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
                minLength="6"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                variant="success" 
                type="submit" 
                disabled={loading || !verificationCode || !newPassword || !confirmPassword}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Resetting Password...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Reset Password
                  </>
                )}
              </Button>
              <Button 
                variant="outline-secondary" 
                onClick={handleBack} 
                disabled={loading}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ForgotPasswordModal;

